import { useState, type ReactNode } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  EntityHeader, TabBar, SectionCard, DataTable, Button, Checkbox, useToast, formatDate, type Column,
} from '@datahub/design-system'
import { tabsForCycle, formKeyFor, allSectionFields, type EntityType, type FieldDef } from '@datahub/shared'
import { get, patch, downloadCsv } from '../../lib/api'
import { entityPath } from '../../lib/paths'
import { StatusTag } from '../../components/StatusTag'
import { ExternalLink } from '../../components/ExternalLink'
import { AttachmentsTable } from './attachments-table'

interface SubmissionRowView {
  id: string
  entity: string
  tab: string
  section: string
  values: Record<string, unknown>
  confirmationState: string
  publicationPermission: string
  generatedTitle: string | null
  copiedToCore: boolean
  coreRecordId: string | null
  copiedToCoreAt: string | null
}

interface IntakeDeliveryView {
  status: string
  attempts: number
  lastError: string | null
  deliveredAt: string | null
  idempotencyKey: string
}

interface DetailData {
  submission: {
    id: string; reference: string; version: number; submittedAt: string
    submittedByName: string; submittedByEmail: string
    company: { id: string; displayName: string; entityType: EntityType }
    periodLabel: string
    requestedTabs: string[]
    tabFlags: Record<string, Record<string, boolean>>
    completionScores: Record<string, { pct: number; skipped: boolean }>
    rowCounts: Record<string, number>
    confidentialCounts: Record<string, number>
    copiedToCoreStatus: string
    payload: { tabs: Record<string, { values: Record<string, unknown>; note: string }> }
  }
  rows: SubmissionRowView[]
  notes: { id: string; tab: string; noteText: string; authorName: string | null }[]
  attachments: { id: string; fileName: string; sizeBytes: number; documentType: string | null; virusScanStatus: string; uploadedAt: string }[]
  versions: { id: string; version: number; reference: string; submittedAt: string }[]
  intakeDeliveries: IntakeDeliveryView[]
}

/** Spec 5.2.6: the Workbench delivery line under the submission header. */
function intakeDeliveryLine(delivery: IntakeDeliveryView): string {
  if (delivery.status === 'delivered') return `Workbench: delivered on ${delivery.deliveredAt ? formatDate(delivery.deliveredAt) : 'unknown date'}`
  if (delivery.status === 'failed') return `Workbench: failed. ${delivery.lastError ?? ''}`
  return `Workbench: waiting (attempt ${delivery.attempts})`
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '-'
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'object' && x && 'name' in x ? String((x as { name: string }).name) : String(x))).join(', ')
  if (typeof v === 'object') {
    const o = v as { amount?: number | null; currency?: string; undisclosed?: boolean; value?: string }
    if (o.undisclosed) return 'Undisclosed'
    if (typeof o.amount === 'number') return `${o.currency ?? ''} ${o.amount.toLocaleString('en-GB')}`.trim()
    if (o.value) return o.value
    return JSON.stringify(v)
  }
  return String(v)
}

/** A web address is shown as a link the admin can follow; everything else as text. */
function renderValue(field: FieldDef | undefined, v: unknown): ReactNode {
  if (field?.input === 'url' && v !== null && v !== undefined && v !== '') return <ExternalLink value={v} />
  return formatValue(v)
}

/** RPT-02: read-only rendering with per-row copy-to-Core tracking. */
export function SubmissionDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('General')

  const { data, isLoading } = useQuery({
    queryKey: ['submission', id],
    queryFn: () => get<DetailData>(`/admin/submissions/${id}`),
    enabled: Boolean(id),
  })

  const markCopied = useMutation({
    mutationFn: (input: { rowId: string; copied: boolean; coreRecordId?: string }) =>
      patch(`/admin/submissions/${id}/rows/${input.rowId}/copied`, { copied: input.copied, coreRecordId: input.coreRecordId ?? null }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['submission', id] }),
    onError: (e: Error) => toast.error(e.message),
  })

  if (isLoading || !data) {
    return <div className="font-mulish text-text-support" style={{ padding: 20, fontSize: 12 }}>Loading…</div>
  }

  const { submission, rows, notes, attachments, versions, intakeDeliveries } = data
  // The page shows the tabs of the cycle, on the form of the entity. A fund manager has
  // Funds and Portfolio tabs. A company has Ownership and Financials tabs. A campaign tab
  // shows only when the cycle requests it.
  const dictionaryTabs = tabsForCycle(formKeyFor(submission.company.entityType), submission.requestedTabs ?? [])
  const shownTabs = dictionaryTabs.filter((t) => t.key !== 'review')
  const tabNames = shownTabs.map((t) => t.shortLabel)
  // A narrowed cycle can leave out General, so the first tab shown is the default.
  const tabDef = shownTabs.find((t) => t.shortLabel === activeTab) ?? shownTabs[0] ?? dictionaryTabs[0]!
  const activeKey = tabDef.key
  const tabRows = rows.filter((r) => r.tab === activeKey && r.entity !== 'profile')
  const profileRow = rows.find((r) => r.tab === activeKey && r.entity === 'profile')
  const tabNote = notes.find((n) => n.tab === activeKey)
  const flags = submission.tabFlags[activeKey] ?? {}

  const fields = new Map(
    tabDef.sections.flatMap((s) => allSectionFields(s).map((f) => [f.key, f] as const)),
  )

  const rowColumns: Column<SubmissionRowView>[] = [
    { key: 'summary', header: 'Entry', width: 340, sticky: true,
      render: (r) => r.generatedTitle ?? formatValue(Object.values(r.values)[0]) },
    { key: 'state', header: 'Provenance', width: 150,
      render: (r) => (r.confirmationState === 'confirmed' ? 'Confirmed' : r.confirmationState === 'edited' ? 'Edited' : 'New') },
    { key: 'permission', header: 'Publication', width: 140,
      render: (r) => (r.publicationPermission === 'confidential' ? <StatusTag status="confidential" /> : 'May publish') },
  ]

  return (
    <div className="flex flex-col bg-background-body" style={{ padding: 20, height: '100%' }}>
      <EntityHeader name={`${submission.company.displayName} · ${submission.periodLabel}`} type={`Submission ${submission.reference}`} />
      {intakeDeliveries.length > 0 && (
        <div className="flex flex-col" style={{ gap: 4, padding: '8px 0' }}>
          {intakeDeliveries.map((delivery) => (
            <div key={delivery.idempotencyKey} className="flex items-center" style={{ gap: 8 }}>
              <StatusTag status={delivery.status} />
              <span className="font-mulish text-white" style={{ fontSize: 12 }}>{intakeDeliveryLine(delivery)}</span>
            </div>
          ))}
        </div>
      )}
      <TabBar
        tabs={tabNames}
        active={tabDef.shortLabel}
        onChange={setActiveTab}
        rightSlot={
          <div className="flex items-center" style={{ gap: 8 }}>
            <StatusTag status={submission.copiedToCoreStatus} />
            <Button variant="outline" size="extra-small" onClick={() => void downloadCsv(`/admin/submissions/${submission.id}/export/${activeKey}`, `${activeKey}.csv`)}>Export tab</Button>
            <Button variant="outline" size="extra-small" onClick={() => navigate(entityPath(submission.company.entityType, submission.company.id))}>
              {submission.company.entityType === 'company' ? 'Company' : 'Fund'}
            </Button>
          </div>
        }
      />

      <div
        className="flex-1 overflow-y-auto bg-background-main"
        style={{ padding: 20, borderLeft: '0.5px solid #4B5563', borderRight: '0.5px solid #4B5563', borderBottom: '0.5px solid #4B5563' }}
      >
        <div className="flex flex-col" style={{ gap: 20 }}>
          <div className="grid grid-cols-2 items-start gap-5">
            <SectionCard title="Submission">
              <div className="flex flex-col">
                {[
                  ['Reference', submission.reference],
                  ['Version', String(submission.version)],
                  ['Submitted', formatDate(submission.submittedAt)],
                  ['Submitted by', `${submission.submittedByName} <${submission.submittedByEmail}>`],
                  ['Tab completion', `${submission.completionScores[activeKey]?.pct ?? 0}%`],
                  ['Versions', versions.map((v) => `v${v.version}`).join(', ')],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center" style={{ padding: '8px 20px', borderBottom: '0.5px solid #4B5563' }}>
                    <span className="font-mulish text-white" style={{ fontSize: 12, width: '42%' }}>{label}</span>
                    <span className="font-mulish text-white" style={{ fontSize: 12, flex: 1 }}>{value}</span>
                  </div>
                ))}
              </div>
            </SectionCard>

            {tabNote && (
              <SectionCard title="Notes and clarifications">
                <p className="font-mulish text-white" style={{ fontSize: 12, lineHeight: '22px', padding: '12px 20px', whiteSpace: 'pre-wrap' }}>
                  {tabNote.noteText}
                </p>
              </SectionCard>
            )}
          </div>

          {Object.entries(flags).some(([, v]) => v) && (
            <p className="font-mulish text-text-support" style={{ fontSize: 12 }}>
              The company marked this tab: {Object.entries(flags).filter(([, v]) => v).map(([k]) => k.replace(/_/g, ' ')).join(', ')}.
            </p>
          )}

          {profileRow && (
            <SectionCard title={`${tabDef.label} — answers`}>
              <div className="flex flex-col">
                {Object.entries(profileRow.values).map(([key, value]) => {
                  const fieldKey = key.includes('.') ? key.slice(key.indexOf('.') + 1) : key
                  const field = fields.get(fieldKey)
                  return (
                    <div key={key} className="flex items-start" style={{ padding: '8px 20px', borderBottom: '0.5px solid #4B5563' }}>
                      <span className="font-mulish text-white flex-shrink-0" style={{ fontSize: 12, width: '42%' }}>
                        {field?.label ?? fieldKey}
                      </span>
                      <span className="font-mulish text-white" style={{ fontSize: 12, flex: 1 }}>{renderValue(field, value)}</span>
                    </div>
                  )
                })}
                <div className="flex items-center" style={{ padding: '8px 20px' }}>
                  <span className="font-mulish text-white" style={{ fontSize: 12, width: '42%' }}>Copied to Core</span>
                  <span style={{ flex: 1 }}>
                    <Checkbox checked={profileRow.copiedToCore} onChange={(copied) => markCopied.mutate({ rowId: profileRow.id, copied })} />
                  </span>
                </div>
              </div>
            </SectionCard>
          )}

          {tabRows.length > 0 && (
            <SectionCard title={`${tabDef.label} — entries`}>
              <DataTable columns={rowColumns} rows={tabRows} rowKey={(r) => r.id} caption="Submitted rows" emptyMessage="Nothing recorded on this tab" />
            </SectionCard>
          )}

          {tabRows.length > 0 && (
            <SectionCard title="Entry detail">
              <div className="flex flex-col">
                {tabRows.map((row) => (
                  <div key={row.id} style={{ borderBottom: '0.5px solid #4B5563', padding: '12px 20px' }}>
                    <p className="font-mulish font-bold text-white" style={{ fontSize: 12, marginBottom: 8 }}>
                      {row.generatedTitle ?? formatValue(Object.values(row.values)[0])}
                    </p>
                    <div className="grid grid-cols-2 gap-x-5">
                      {Object.entries(row.values).map(([key, value]) => (
                        <div key={key} className="flex" style={{ padding: '3px 0', gap: 8 }}>
                          <span className="font-mulish text-text-support" style={{ fontSize: 12, width: '45%' }}>
                            {fields.get(key)?.label ?? key}
                          </span>
                          <span className="font-mulish text-white" style={{ fontSize: 12, flex: 1 }}>{renderValue(fields.get(key), value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {activeKey === 'documents' && (
            <SectionCard title="Attachments">
              <AttachmentsTable
                attachments={attachments}
                onDownload={async (a) => {
                  try {
                    const res = await get<{ url: string }>(`/admin/attachments/${a.id}/download`)
                    // The URL answers with "Content-Disposition: attachment", so the
                    // page stays open. A popup opened after an await can be blocked.
                    window.location.assign(res.url)
                  } catch (e) {
                    toast.error(e instanceof Error ? e.message : `We could not open ${a.fileName}`)
                  }
                }}
              />
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  )
}
